import type * as React from "react";
import { Slot } from "radix-ui";

import { cn } from "@/lib/utils";

import { RippleButton } from "./ripple-button";

import {
  CHIP_CLICKABLE,
  CHIP_CLICKABLE_PRIMARY,
  CHIP_FILLED,
  CHIP_ICON,
  CHIP_ICON_OUTLINED,
  CHIP_ICON_SMALL,
  CHIP_ICON_SMALL_OUTLINED,
  CHIP_LABEL,
  CHIP_OUTLINED,
  CHIP_OUTLINED_OVER_TARGET,
  CHIP_OUTLINED_PRIMARY,
  CHIP_ROOT,
  CHIP_ROOT_SMALL,
} from "./chip-shape";

// MUI's `Chip`, ported — the clickable outlined one, which is what both of the
// item details page's variant rows draw. The numbers, and why they are MUI's, are
// in `chip-shape.ts`.
//
// **Shared rather than page-local** (`docs/agents/migration-guidelines.md`,
// "Shared donor components port as shared Atlas components").
//
// The structure is MUI's, and it is load-bearing rather than incidental: the
// **padding lives on the label**, not on the root (`Chip.js:312-330`). That is
// what lets a caller clamp a chip's width and get an ellipsis instead of a
// clipped inset — which the details page's Color row does.

/**
 * Everything a `<button>` takes, plus MUI's chip props.
 *
 * The passthrough is not generosity: the Color row wraps each chip in a Radix
 * tooltip trigger with `asChild`, which works by *handing the child* its ref and
 * its hover and focus handlers. A component that swallowed them would leave the
 * tooltip silently dead.
 */
interface ChipProps extends Omit<React.ComponentProps<"button">, "children" | "type"> {
  label: string;
  /**
   * MUI's `color`. `primary` is how both variant rows mark the chip whose item is
   * the one on screen — the donor passes exactly this and nothing else, so the
   * highlight is a palette colour rather than a bespoke selected style.
   *
   * `over-target` is "Where to save"'s share chip (#1217), the donor's red
   * `sx` over the default colour (`CHIP_OUTLINED_OVER_TARGET`).
   */
  color?: "default" | "primary" | "over-target";
  /**
   * MUI's `icon`, seated before the label at the medium outlined slot (4px in,
   * 6px into the label). It takes the chip's ink: its one caller passes
   * `'> .MuiChip-icon': { color: 'inherit' }`, so MUI's `grey[700]` default
   * has no caller to port for.
   */
  icon?: React.ReactNode;
  onClick: NonNullable<React.ComponentProps<"button">["onClick"]>;
}

/** Each colour's outlined look and its clickable grounds. */
const BY_COLOR = {
  default: { outlined: CHIP_OUTLINED, clickable: CHIP_CLICKABLE },
  primary: { outlined: CHIP_OUTLINED_PRIMARY, clickable: CHIP_CLICKABLE_PRIMARY },
  "over-target": { outlined: CHIP_OUTLINED_OVER_TARGET, clickable: CHIP_CLICKABLE },
} as const;

/**
 * A clickable **outlined** chip — MUI's `variant='outlined'`, which is the only
 * variant with a `Chip` caller. The filled look is `chip-list.tsx`, which
 * composes `CHIP_FILLED` directly rather than going through here.
 *
 * Always a `<button>`: a `Chip` handed an `onClick` *is* a `ButtonBase` in MUI,
 * which means a real button element, keyboard activation and a ripple. A chip
 * with no `onClick` is a different thing and has no caller here — hence `onClick`
 * being the one button prop this narrows to required.
 */
export function Chip({ label, color = "default", icon, className, ...rest }: ChipProps) {
  return (
    // A `RippleButton`, which is this app's `ButtonBase`: the `<button>`, the
    // hook, the layer and the eleven-arm composition are all its, and what is
    // left here is the chip's own look. Handing the handlers down rather than
    // composing them here matters for more than tidiness — this chip is a
    // tooltip trigger under `ItemFinishVariants`, and Radix sends its own
    // handlers through `asChild`. What they carry is no longer a
    // dismiss-on-press (`MuiTooltip` declines that app-wide, #480) but the
    // press and focus bookkeeping the decline itself depends on, so clobbering
    // any of them still costs the tooltip its behaviour.
    //
    // **No `focusRipple`**, and that is a source fact rather than an omission:
    // `Chip.js` never mentions the prop, so `ButtonBase`'s `false` default
    // stands and a focused chip stays silent under Space while still inking
    // under the pointer.
    <RippleButton
      {...rest}
      className={cn(CHIP_ROOT, BY_COLOR[color].outlined, BY_COLOR[color].clickable, className)}
    >
      {icon ? (
        <Slot.Root aria-hidden="true" className={cn(CHIP_ICON, CHIP_ICON_OUTLINED)}>
          {icon}
        </Slot.Root>
      ) : null}
      <span data-slot="chip-label" className={cn(CHIP_LABEL, "px-[11px]")}>
        {label}
      </span>
    </RippleButton>
  );
}

/**
 * MUI's label padding by size and variant (`Chip.mjs`): the outlined border
 * takes a pixel of it.
 */
const STATIC_LABEL_PADDING = {
  small: { filled: "px-2", outlined: "px-[7px]" },
  medium: { filled: "px-3", outlined: "px-[11px]" },
} as const;

interface StaticChipProps extends Omit<React.ComponentProps<"span">, "children"> {
  label: React.ReactNode;
  /** MUI's `size`; `medium` when omitted, as MUI defaults it. */
  size?: "small" | "medium";
  /** MUI's `variant`; `filled` when omitted, as MUI defaults it. */
  variant?: "filled" | "outlined";
  /**
   * MUI's `icon`: a glyph seated before the label at MUI's slot margins. The
   * glyph carries its own ink, as the donor's status icons do.
   */
  icon?: React.ReactNode;
}

/**
 * A chip with nothing to do — MUI's `Chip` with no `onClick`, which renders a
 * plain box with no role, no tab stop and no ripple.
 *
 * The status chips are these: the donor's `ItemStatusChip` and
 * `ItemStatusLabelInternal` are both `size='small'` filled chips with a
 * leading icon (`src/components/item-status-chip`,
 * `domains/items/components/item-status-label`), and the specifications screens
 * bring three more (`DraftChip`, `SpecificationChip`, `SpecificationStatus`).
 * The catalog had drawn its three on the vendored shadcn `Badge` — 12px type
 * about 20px tall, with 12px icons — which is a different component rather than
 * a different colour of this one.
 *
 * **Colour is the caller's**, through `className`: every donor status chip
 * passes its own ground and ink (`backgroundColor`/`textColor` props, or a
 * palette key the donor theme adds), so a colour vocabulary here would be a
 * list of one-caller entries. Without one it is MUI's default filled chip,
 * `action.selected` under `text.primary`.
 *
 * A `<span>` where MUI has a `<div>`: the chips sit inside table cells and
 * tooltip triggers, where a block element is invalid in a phrasing context,
 * and the chip is `inline-flex` either way.
 */
export function StaticChip({
  label,
  size = "medium",
  variant = "filled",
  icon,
  className,
  ...rest
}: StaticChipProps) {
  return (
    <span
      data-slot="chip"
      {...rest}
      className={cn(
        CHIP_ROOT,
        size === "small" && CHIP_ROOT_SMALL,
        variant === "outlined" ? CHIP_OUTLINED : CHIP_FILLED,
        className,
      )}
    >
      {icon ? (
        <Slot.Root
          aria-hidden="true"
          className={cn(
            size === "small" ? CHIP_ICON_SMALL : CHIP_ICON,
            variant === "outlined" &&
              (size === "small" ? CHIP_ICON_SMALL_OUTLINED : CHIP_ICON_OUTLINED),
          )}
        >
          {icon}
        </Slot.Root>
      ) : null}
      {/* `leading-normal` again on the label, not only the root's: MUI's 1.5 is
          the root's (`Chip.mjs:75`), and a caller's font size — the status
          chips' `0.8rem` — makes `tailwind-merge` drop the root's copy, which
          would leave the label on whatever line its parent has. */}
      <span
        data-slot="chip-label"
        className={cn(CHIP_LABEL, "leading-normal", STATIC_LABEL_PADDING[size][variant])}
      >
        {label}
      </span>
    </span>
  );
}
